import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import cx from "classnames";

import Tooltip from "src/components/tooltip";
import styles from "./common.module.scss";

interface TableCellTextProps {
  text: string;
  className?: string;
  /** Wrap to the next line instead of single-line ellipsis. */
  wrap?: boolean;
}

const isOverflowing = (el: HTMLElement) => el.scrollWidth > el.clientWidth + 1;

/**
 * Single-line ellipsis for table cells; tooltip only when text is actually clipped.
 */
export const TableCellText: React.FC<TableCellTextProps> = ({ text, className, wrap }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);

  const updateTruncation = useCallback(() => {
    if (wrap) return;
    const el = ref.current;
    if (!el) return;
    setTruncated(isOverflowing(el));
  }, [wrap]);

  useLayoutEffect(() => {
    updateTruncation();
    const el = ref.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(updateTruncation);
    });
    observer.observe(el);

    if (el.parentElement) {
      observer.observe(el.parentElement);
    }

    return () => observer.disconnect();
  }, [text, updateTruncation]);

  if (!text) return null;

  return (
    <Tooltip placement="top" content={text} disabled={wrap || !truncated}>
      <div
        ref={ref}
        className={cx(
          styles.tableCellText,
          wrap && styles.tableCellTextWrap,
          className,
        )}
      >
        {text}
      </div>
    </Tooltip>
  );
};
